import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, MoveDown, Sparkles } from "lucide-react";
import { EASE } from "./Reveal";
import { IMAGES } from "../data";

const Sparkle = ({ className, size = 22 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0c.8 6.6 5.4 11.2 12 12-6.6.8-11.2 5.4-12 12-.8-6.6-5.4-11.2-12-12C6.6 11.2 11.2 6.6 12 0Z" />
  </svg>
);

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 1.15, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Fade = ({ children, delay, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay, ease: EASE }}>
    {children}
  </motion.div>
);

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(mx, { stiffness: 80, damping: 16 });
  const rotateX = useSpring(my, { stiffness: 80, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 9);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 9);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section className="hero" id="beranda" ref={ref}>
      <div className="container hero-grid">
        <div className="hero-copy">
          <Fade delay={0.15}>
            <span className="pill">
              <Sparkles size={13} /> JASA BERSIH RUMAH &amp; KANTOR
            </span>
          </Fade>
          <h1>
            <MaskedLine delay={0.3}>Bersih kinclong,</MaskedLine>
            <MaskedLine delay={0.45}>
              rumah <em>happy.</em>
            </MaskedLine>
          </h1>
          <Fade delay={0.65}>
            <p className="hero-sub">
              Rumah yang bersih bukan cuma enak dipandang — ia membuat hari terasa lebih ringan. Serahkan urusan bersih-bersih pada tim
              antarcleaning, dan nikmati waktu Anda untuk hal yang lebih penting.
            </p>
          </Fade>
          <Fade delay={0.8} className="hero-cta">
            <a href="#pesan" className="btn-primary" data-testid="hero-booking-link">
              Booking sekarang <ArrowRight size={17} />
            </a>
            <a href="#cara-kerja" className="btn-text" data-testid="hero-process-link">
              Lihat cara kerja <ArrowRight size={15} />
            </a>
          </Fade>
          <Fade delay={0.95} className="hero-proof">
            <div className="avatar-stack">
              <span>NS</span>
              <span>RA</span>
              <span>+</span>
            </div>
            <div>
              <strong>Dipercaya 500+ rumah</strong>
              <small>Rating 4.9/5 dari pelanggan</small>
            </div>
          </Fade>
        </div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.94, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
        >
          <motion.div
            className="hero-frame"
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          >
            <motion.img src={IMAGES.teamWorking} alt="Petugas antarcleaning sedang membersihkan ruang keluarga" style={{ y: yImg }} data-testid="hero-team-image" />
          </motion.div>

          <motion.div
            className="hero-chip"
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <BadgeCheck size={19} />
            <span>
              <strong>Petugas terverifikasi</strong>
              <small>Terlatih &amp; terseleksi</small>
            </span>
          </motion.div>

          <div className="hero-badge" aria-hidden="true">
            <svg className="badge-spin" viewBox="0 0 132 132">
              <defs>
                <path id="badge-circle" d="M66,66 m-50,0 a50,50 0 1,1 100,0 a50,50 0 1,1 -100,0" fill="none" />
              </defs>
              <text>
                <textPath href="#badge-circle">ANTAR CLEANING • BERSIH KINCLONG • RUMAH HAPPY •</textPath>
              </text>
            </svg>
            <img src={IMAGES.logoMark} alt="" />
          </div>

          <Sparkle className="sparkle sp-1" size={26} />
          <Sparkle className="sparkle sp-2" size={16} />
          <Sparkle className="sparkle sp-3" size={20} />
        </motion.div>
      </div>
      <Fade delay={1.2} className="scroll-cue">
        <MoveDown size={15} /> Gulir untuk menjelajah
      </Fade>
    </section>
  );
}
